import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  Typography,
  Box,
  TextField,
  InputAdornment
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';

import logger from '../utils/logger.js';
const SimpleFileBrowser = ({ open, onClose, onFileSelect }) => {
  const [files, setFiles] = useState([]);
  const [filteredFiles, setFilteredFiles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      // Reset search and load files when opening
      setSearchTerm('');
      loadFiles();
      // Focus the search input after dialog mounts
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 0);
    }
  }, [open]);

  useEffect(() => {
    if (searchTerm.length < 3) {
      setFilteredFiles([]);
      return;
    }
    const lowered = searchTerm.toLowerCase();
    const filtered = files.filter((file) =>
      file.name.toLowerCase().includes(lowered) ||
      file.path.toLowerCase().includes(lowered)
    );
    setFilteredFiles(filtered);
  }, [files, searchTerm]);

  const loadFiles = async () => {
    setLoading(true);
    try {
      const response = await fetch('/.netlify/functions/list-files');
      const data = await response.json();
      if (data.files) {
        setFiles(data.files);
      }
    } catch (error) {
      console.error('Error loading files:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (filePath) => {
    onFileSelect(filePath);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Select Image File</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          placeholder="Search files..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          label="Search files"
          inputRef={inputRef}
          inputProps={{ 'aria-label': 'Search files' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2, mt: 1 }}
        />
        
        {loading ? (
          <Typography>Loading files...</Typography>
        ) : searchTerm.length < 3 ? (
          <Typography color="text.secondary">
            Type at least 3 characters to search.
          </Typography>
        ) : filteredFiles.length === 0 ? (
          <Typography color="text.secondary">No files found</Typography>
        ) : (
          <List sx={{ maxHeight: 400, overflow: 'auto' }}>
            {filteredFiles.map((file, index) => (
              <ListItem
                key={index}
                button
                onClick={() => handleFileSelect(file.path)}
                sx={{
                  border: '1px solid #ddd',
                  mb: 1,
                  borderRadius: 1,
                  '&:hover': { bgcolor: 'action.hover' }
                }}
              >
                <ListItemText
                  primary={file.name}
                  secondary={file.path}
                />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

SimpleFileBrowser.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onFileSelect: PropTypes.func.isRequired
};

export default SimpleFileBrowser;
